import * as WeightUtils from "~/utils/WeightUtils";
import { type WeightData } from "~/domains/product/types/WeightData";
import { transformAddress } from "~/features/cart/cart/transformers/CheckoutViewDataTransformer";

import { DEFAULT_UNIT_SYMBOL } from "~/utils/constants";
import { adjustFormat } from "~/utils/accountNumberUtils";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import { getDate } from "~/common/utils/ApiHelper";
import {
  type DeliveryEntryData,
  type HandlingUnitQuantityData,
  type ShipmentLineData,
} from "~/domains/orders/types/models";
import { resolveUrl } from "~/utils/url/urlResolver";
import { type DetailedOrderData, type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import {
  constructOrderDetailsUrl,
  getLineItemLineNo,
  processOrderLineItems,
  transformOrderAccount,
} from "~/features/orders/orderList/api/fetchOrderHistory/fetchOrderHistory";
import {
  type DeliveryEntryDTO,
  type HandlingUnitQuantityDTO,
  type OrderDetailsDTO,
  type OrderEntryDTO,
  type ShipmentLineDTO,
} from "~/features/orders/orderList/api/fetchOrderDetails/dto";
import { type ProductData } from "~/domains/product/types/ProductData";
import { type NetPriceData } from "~/domains/pricing/types/PriceData";
import { isSubscription } from "~/utils/productUtils";
import { isSurchargeIncludedInPriceValue } from "~/utils/features";
import { type QuoteLineItemDTO } from "~/features/orders/quoteList/api/fetchOrderDetails/dto";
import { type OrderLineItemDTO } from "~/features/orders/orderList/api/fetchOrderHistory/dto";
import * as PriceUtils from "~/utils/PriceUtils";
import { getItemStatus } from "~/domains/orders/types/GetItemStatus";

// soNumber === salesDocument
export async function fetchOrderDetails(
  soNumber: string,
  accountNumber?: string,
  salesOrg?: string,
): Promise<DetailedOrderData> {
  let url = resolveUrl(`/my-account/order/${soNumber}`);

  if (accountNumber && salesOrg) {
    const params = new URLSearchParams();
    params.append("account", accountNumber);
    params.append("salesOrg", salesOrg);
    url = url + "?" + params.toString();
  }

  const response = await fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });

  const dto = (await response.json()) as OrderDetailsDTO;
  return transformOrderDetails(dto);
}

export function transformOrderDetails(data: OrderDetailsDTO): DetailedOrderData {
  const orderEntries = data.orderEntries ?? [];
  processOrderLineItems(orderEntries);

  const entries: OrderLineItemData[] = orderEntries.map(transformOrderLineItem);

  const orderWeight = data.orderWeight !== null ? data.orderWeight : 0;
  const orderWeightUnit = data.orderWeightUnit !== null ? data.orderWeightUnit : DEFAULT_UNIT_SYMBOL;
  const totalWeight: WeightData = WeightUtils.getWeightObject(orderWeight, orderWeightUnit);

  const accNo = adjustFormat(data.accountNumber ?? "");

  return {
    detailsUrl: constructOrderDetailsUrl(data.soNumber, data),
    poNumber: data.poNumber,
    soNumber: data.soNumber,
    datePlaced: data.datePlaced ? new Date(data.datePlaced) : undefined,
    totalPrice: data.orderTotal,
    orderStatus: data.orderStatus,
    orderStatusCode: data.orderStatusCode,
    orderType: data.orderType,
    products: entries,

    accNo,
    shipmentMethod: data.shipmentMethod || "-",
    freightPayment: data.freightPaymentType,
    soldTo: data.soldToAddress ? transformAddress(data.soldToAddress) : undefined,
    shippingAddress: data.shipToAddress ? transformAddress(data.shipToAddress) : undefined,
    totalWeight,
    deliveryEntries: (data.deliveryEntries ?? []).map(transformDeliveryEntryItem),
    comment: data.comment || "",
    unloadingPoint: data.unloadingPoint || "",
    completeDelivery: data.completeDelivery || false,
    account: transformOrderAccount(data.account),
    hasSurcharge: data.hasSurcharge ?? false,
    orderCnfDocumentUrl: data.orderCnfDocumentUrl || "",
  };
}

export function getNetPrice(
  data: OrderEntryDTO | QuoteLineItemDTO | OrderLineItemDTO,
  product: ProductData,
): NetPriceData {
  const surcharge = data.surcharge ?? 0;
  const baseValue = data.nettoPrice.value;
  return {
    value: isSurchargeIncludedInPriceValue() ? baseValue : baseValue * (1 + surcharge),
    baseValue,
    currencyIso: data.nettoPrice.currencyIso,
    unit: +(data.priceUnit || "") || 1,
    surcharge,
    isSubscription: isSubscription(product),
    rebate: undefined,
    isSpecial: undefined,
  };
}

export function transformOrderLineItem(data: OrderEntryDTO): OrderLineItemData {
  const mass = data.massPerPiece !== null ? data.massPerPiece : 0;
  const unit = data.massUnit !== null ? data.massUnit : DEFAULT_UNIT_SYMBOL;

  const weight = WeightUtils.getWeightObject(mass, unit);

  const product = transformProduct(data.product);

  const statusEnum = getItemStatus(data.lineItemStatusCode);

  return {
    lineNo: getLineItemLineNo(data),
    lineNumber: data.lineNumber ?? 0,
    scheduleLineNumber: data.scheduleLineNumber ?? 0,
    comment: data.comment || "",
    qty: data.quantityRequested,
    confirmedQty: data.quantityConfirmed,
    shippedQty: data.quantityShipped,
    packCode: data.packCode,
    packSize: data.packSize ?? 1,
    deliveryDate: getDate(data.deliveryDate),
    requestedDeliveryDate: getDate(data.requestedDeliveryDate),
    weight,
    netPrice: getNetPrice(data, product),
    priceForTotal: data.priceForTotal || PriceUtils.zero(),
    status: data.lineItemStatus,
    statusCode: data.lineItemStatusCode,
    invoiceData:
      !data.shipmentInformation || !data.shipmentInformation.shippingPartnerID
        ? undefined
        : {
            trackingUrl: data.shipmentInformation.trackingUrl ? data.shipmentInformation.trackingUrl : undefined,
            invoiceDate: data.shipmentInformation.invoiceDate || "",
            type: data.shipmentInformation.shippingPartnerName || "",
            invoiceNo: data.shipmentInformation.invoiceNumber || "",
            invoiceUrl: data.shipmentInformation.invoiceUrl ? resolveUrl(data.shipmentInformation.invoiceUrl) : null,
            trackingNo: data.shipmentInformation.trackingNumber || "-",
            shippingPartnerId: data.shipmentInformation.shippingPartnerID || "",
            shippingPartnerName: data.shipmentInformation.shippingPartnerName || "",
            deliveryNoteNumber: data.shipmentInformation.deliveryNoteNumber || "",
            deliveryNoteUrl: data.shipmentInformation.deliveryNoteUrl
              ? resolveUrl(data.shipmentInformation.deliveryNoteUrl)
              : null,
          },
    quoteNumber: data.quoteNumber || "",
    notaFiscal:
      data.shipmentInformation && data.shipmentInformation.notaFiscal ? data.shipmentInformation.notaFiscal : null,
    product,
    location: data.primaryWarehouseName,
    contractNumber: data.contractNumber ?? "",
    contractLine: data.contractLine ?? "",
    priceReference: data.priceReference || "",
    orderable: data.orderable,
    customerOrderNumber: data.customerOrderNumber ?? "",
    logisticalComment: data.logisticalComment ?? "",
    mainQuotes: [],
    statusEnum: statusEnum,
    subEntries: data.subEntries?.map((item) => transformOrderSubEntries(item, product)) ?? [],
  };
}

export function transformOrderSubEntries(
  data: OrderEntryDTO | OrderLineItemDTO,
  product: ProductData,
): OrderLineItemData {
  const mass = data.massPerPiece !== null ? data.massPerPiece : 0;
  const unit = data.massUnit !== null ? data.massUnit : DEFAULT_UNIT_SYMBOL;

  const weight = WeightUtils.getWeightObject(mass, unit);

  return {
    lineNo: getLineItemLineNo(data),
    lineNumber: data.lineNumber ?? 0,
    scheduleLineNumber: data.scheduleLineNumber ?? 0,
    comment: data.comment || "",
    qty: data.quantityRequested,
    confirmedQty: data.quantityConfirmed,
    shippedQty: data.quantityShipped,
    packCode: data.packCode,
    packSize: data.packSize ?? 1,
    deliveryDate: getDate(data.deliveryDate),
    requestedDeliveryDate: getDate(data.requestedDeliveryDate),
    weight,
    netPrice: getNetPrice(data, product),
    priceForTotal: data.priceForTotal || PriceUtils.zero(),
    status: data.lineItemStatus,
    statusCode: data.lineItemStatusCode,
    invoiceData:
      !data.shipmentInformation || !data.shipmentInformation.shippingPartnerID
        ? undefined
        : {
            trackingUrl: data.shipmentInformation.trackingUrl ? data.shipmentInformation.trackingUrl : undefined,
            invoiceDate: data.shipmentInformation.invoiceDate || "",
            type: data.shipmentInformation.shippingPartnerName || "",
            invoiceNo: data.shipmentInformation.invoiceNumber || "",
            invoiceUrl: data.shipmentInformation.invoiceUrl ? resolveUrl(data.shipmentInformation.invoiceUrl) : null,
            trackingNo: data.shipmentInformation.trackingNumber || "-",
            shippingPartnerId: data.shipmentInformation.shippingPartnerID || "",
            shippingPartnerName: data.shipmentInformation.shippingPartnerName || "",
            deliveryNoteNumber: data.shipmentInformation.deliveryNoteNumber || "",
            deliveryNoteUrl: data.shipmentInformation.deliveryNoteUrl
              ? resolveUrl(data.shipmentInformation.deliveryNoteUrl)
              : null,
          },
    quoteNumber: data.quoteNumber || "",
    notaFiscal:
      data.shipmentInformation && data.shipmentInformation.notaFiscal ? data.shipmentInformation.notaFiscal : null,
    product,
    location: data.primaryWarehouseName,
    contractNumber: "contractNumber" in data ? data?.contractNumber ?? "" : "",
    contractLine: "contractLine" in data ? data?.contractLine ?? "" : "",
    priceReference: data.priceReference || "",
    orderable: data.orderable,
    customerOrderNumber: data.customerOrderNumber ?? "",
    logisticalComment: data.logisticalComment ?? "",
    mainQuotes: [],
    subEntries: [],
    statusEnum: getItemStatus(data.lineItemStatusCode),
  };
}

function transformDeliveryEntryItem(data: DeliveryEntryDTO): DeliveryEntryData {
  return {
    deliveryDate: getDate(data.deliveryDate),
    deliveryNumber: data.deliveryNumber ?? "",
    shipmentPartnerID: data.shipmentPartnerID ?? "",
    shipmentStatus: data.shipmentStatus ?? "",
    transportNumber: data.transportNumber ?? "",
    weight: WeightUtils.getWeightObject(data.weight, data.weightUnit),
    packages: transformDeliveryPackages(data.packages),
    actualGIDate: getDate(data.actualGIDate),
    plannedGIDate: getDate(data.plannedGIDate),
  };
}

function transformShipmentLineItem(data: ShipmentLineDTO): ShipmentLineData {
  return {
    lineNumber: data.lineNumber,
    scheduleLineNumber: data.scheduleLineNumber,
    externalHandlingUnitId: data.externalHandlingUnitId ?? "",
    totalWeight: WeightUtils.getWeightObject(data.totalWeight, data.weightUnit),
    weightUnit: data.weightUnit ?? "",
    handlingUnitQuantity: transformHandlingUnitQuantity(data.handlingUnitQuantity),
    validHandlingUnit: data.validHandlingUnit,
  };
}

function transformHandlingUnitQuantity(data: HandlingUnitQuantityDTO): HandlingUnitQuantityData {
  return {
    quantityValue: data.quantityValue,
    quantityUnit: data.quantityUnit ?? "",
  };
}

function transformDeliveryPackages(data: Dict<ShipmentLineDTO[]> | null): Dict<ShipmentLineData[]> {
  if (data === null) {
    return {};
  } else {
    const packages: Dict<ShipmentLineData[]> = {};
    for (const [key, value] of Object.entries(data)) {
      packages[key] = value.map(transformShipmentLineItem);
    }
    return packages;
  }
}
